import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Button,
  Text,
  TextInput,
  PinInput,
  Flex,
  ActionIcon,
} from "@mantine/core";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { IconArrowRight } from "@tabler/icons-react";

import { BasicForm } from "@components/shared";
import { phoneValidation } from "@util/validation";

export default function SmsApiKeyForm({
  phone = "",
  data = "",
  link = "",
  testPhone = "",
  id = null,
  onSuccess = () => {},
  resend = false,
  phoneId = null,
  messagingServiceId = null,
  onPhoneCreate = () => {},
  onServiceCreate = () => {},
  isMessagingService = false,
  reqData = {},
  messageServiceIdent = "",
}) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [tempFormData, setTempFormData] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [remoteCode, setRemoteCode] = useState(null);

  useEffect(() => {
    let timer;
    if (confirm) {
      timer = setTimeout(() => {
        if (!success) {
          onSuccess();
        }
      }, 2 * 60 * 1000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [confirm]);

  function validateData(formData) {
    const req = {
      ...formData,
      ...reqData,
      sms_messaging_method: isMessagingService ? "messaging_service" : "phone",
    };

    setLoading(true);
    // setTempFormData(formData);
    // setConfirm(true);

    const url = isMessagingService
      ? "/add-messaging-service/"
      : "/add-sms-marketing-number/";

    axios
      .post(url, req)
      .then(({ data }) => {
        if (isMessagingService) {
          onServiceCreate(data.response[0].messaging_service_sid);
        } else {
          onPhoneCreate(data.response[0].phone_id);
        }
        setRemoteCode(data.response[0]);
        setTempFormData(formData);
        setConfirm(true);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function resetState() {
    setTempFormData(null);
    setConfirm(false);
    setVerificationCode("");
    setRemoteCode(null);
  }

  function verifyCode(formData) {
    const req = {
      ...formData,
      ...reqData,
      code: verificationCode,
      remote_code_id: remoteCode.remote_code_id,
    };

    setLoading(true);

    axios
      .post(`/microsite/verify-code/`, req)
      .then(({ data }) => {
        toast.success("Verified!");
        setLoading(false);
        setSuccess(true);
        resetState();
        setTimeout(() => {
          setSuccess(false);
        }, 1);
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function onResend(formData) {
    const req = {
      ...formData,
      ...reqData,
      phone_id: phoneId,
      messaging_service_sid: messagingServiceId,
      sms_messaging_method: isMessagingService ? "messaging_service" : "phone",
    };

    setLoading(true);

    axios
      .post(`/resend-sms-marketing-code/`, req)
      .then(({ data }) => {
        setRemoteCode(data.response[0]);
        setTempFormData(formData);
        setConfirm(true);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  const fields = [
    {
      name: "message_service_ident",
      label: "Messaging SID",
      initialValue: messageServiceIdent,
      required: true,
      schema: () => Yup.string().max(100, "Too long!").required("Required"),
    },
    {
      name: "phone_number",
      label: "Twilio Phone Number",
      mask: {
        pattern: "(999) 999-9999",
        placeholder: "(XXX) XXX-XXXX",
      },
      initialValue: phone,
      required: true,
      schema: () =>
        Yup.string()
          .matches(phoneValidation, "Not a valid phone number")
          .required("Required"),
    },
    {
      name: "auth_key",
      label: "Account SID",
      initialValue: data,
      required: true,
      schema: () => Yup.string().max(100, "Too long!").required("Required"),
    },
    {
      name: "auth_token",
      label: "Auth Token",
      initialValue: link,
      disabled: id ? true : false,
      required: true,
      schema: () => Yup.string().max(100, "Too long!").required("Required"),
    },
    {
      name: "test_phone_number",
      label: "Phone Number to receive test message",
      initialValue: testPhone,
      required: true,
      mask: {
        pattern: "(999) 999-9999",
        placeholder: "(XXX) XXX-XXXX",
      },
      schema: () =>
        Yup.string()
          .matches(phoneValidation, "Not a valid phone number")
          .required("Required"),
    },
  ]
    .filter((f) => (id ? !["test_phone_number"].includes(f.name) : true))
    .filter((f) => (resend ? ["test_phone_number"].includes(f.name) : true))
    .filter((f) =>
      isMessagingService
        ? !["phone_number"].includes(f.name)
        : !["message_service_ident"].includes(f.name)
    );

  if (success) return null;

  if (confirm && remoteCode) {
    return (
      <>
        <Text>
          A verification code has been sent to{" "}
          <b>{tempFormData.test_phone_number}</b>. This code is active for 2
          minutes. Please enter the code below.
        </Text>
        {/* <TextInput
          // label="Verification Code"
          mt="lg"
          placeholder="Enter the code here"
          value={verificationCode}
          onChange={(e) => setVerificationCode(e.target.value)}
          maxLength={6}
        /> */}
        <Flex align="center" gap="xs" mt="lg">
          <PinInput
            autoFocus
            oneTimeCode
            length={6}
            placeholder="-"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e)}
          />
          <ActionIcon
            size="lg"
            disabled={verificationCode.length !== 6}
            onClick={() => verifyCode(tempFormData)}
            loading={loading}
            fullWidth
          >
            <IconArrowRight size={18} />
          </ActionIcon>
        </Flex>
        {/* <Button
          size="xs"
          variant="subtle"
          color="gray"
          mt="sm"
          onClick={resetState}
        >
          Cancel
        </Button> */}
      </>
    );
  }

  return (
    <React.Fragment>
      <BasicForm
        buttonProps={{
          fullWidth: !id ? true : false,
        }}
        loading={loading}
        onSubmit={(e) => (resend ? onResend(e) : validateData(e))}
        fields={fields}
        buttonText={resend ? "Send" : "Next"}
      />
    </React.Fragment>
  );
}
